/// <reference types="vite/client" />
/**
 * /errors — failures grouped by action type (issue #24).
 * Sister page to /activity, drills into recurring failure patterns.
 */

import { useCallback, useEffect, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";

interface ErrorGroup {
  action: string;
  agent: string;
  count: number;
  lastSeen: string;
  sampleErrorMessage: string;
}

const SINCE_RANGES: Array<{ label: string; ms: number }> = [
  { label: "24h", ms: 24 * 60 * 60 * 1000 },
  { label: "7d", ms: 7 * 24 * 60 * 60 * 1000 },
  { label: "30d", ms: 30 * 24 * 60 * 60 * 1000 },
  { label: "all", ms: 0 },
];

export function Errors() {
  const [groups, setGroups] = useState<ErrorGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sinceMs, setSinceMs] = useState<number>(SINCE_RANGES[1].ms);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (sinceMs > 0) params.set("since", String(Date.now() - sinceMs));
      const qs = params.toString();
      fetch(`/api/errors/grouped${qs ? `?${qs}` : ""}`, { signal })
        .then((r) => (r.ok ? r.json() : { groups: [] }))
        .then((body) => {
          const typed = body as { groups?: ErrorGroup[] };
          setGroups(Array.isArray(typed.groups) ? typed.groups : []);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          setError(err.message);
          setLoading(false);
        });
    },
    [sinceMs],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  if (error) {
    return <ErrorBanner message={error} onRetry={load} context="errors" />;
  }

  const total = groups.reduce((s, g) => s + g.count, 0);

  return (
    <div className="space-y-6">
      <header className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow">Console · 14</p>
          <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
            <span className="text-[var(--color-text)]">Swarm </span>
            <span className="text-accent-gradient">Errors</span>
          </h2>
          <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
            Failure patterns from <code>swarm_actions.status='failed'</code>, grouped by action type to surface recurring incidents.
          </p>
        </div>

        <div className="flex gap-1" role="group" aria-label="Since range">
          {SINCE_RANGES.map((r) => {
            const active = r.ms === sinceMs;
            return (
              <button
                key={r.label}
                onClick={() => setSinceMs(r.ms)}
                className={`font-mono text-xs px-3 py-2 rounded transition-colors ${
                  active
                    ? "text-[var(--color-accent-glow)]"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                }`}
                style={{
                  background: active ? "var(--color-accent-soft)" : "var(--color-bg-card)",
                  border: `1px solid ${active ? "var(--color-accent)" : "var(--color-border)"}`,
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="panel p-4 sm:p-6">
        <div className="flex items-end justify-between mb-3">
          <p className="eyebrow">{loading ? "Loading…" : `${groups.length} failure types · ${total} total`}</p>
        </div>

        {groups.length === 0 && !loading ? (
          <p className="text-center font-display italic text-2xl text-[var(--color-text-muted)] py-12">
            No failures in this window. 🎉
          </p>
        ) : (
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-[var(--color-text-dim)]">
                <th className="text-left font-normal pb-2">Action</th>
                <th className="text-left font-normal pb-2">Agent</th>
                <th className="text-right font-normal pb-2">Count</th>
                <th className="text-left font-normal pb-2 pl-4">Last seen</th>
                <th className="text-left font-normal pb-2 pl-4">Sample error</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, i) => (
                <tr key={`${g.action}-${g.agent}-${i}`} className="text-[var(--color-text-muted)] border-t border-[var(--color-border)]">
                  <td className="py-2 text-[var(--color-text)]">{g.action}</td>
                  <td>{g.agent}</td>
                  <td className="text-right tabular-nums text-[var(--color-status-error)]">{g.count}</td>
                  <td className="pl-4 tabular-nums">{g.lastSeen.slice(0, 19).replace("T", " ")}</td>
                  <td className="pl-4 text-[var(--color-text-dim)] max-w-xs truncate" title={g.sampleErrorMessage}>
                    {g.sampleErrorMessage || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
